import { useCallback, useEffect, useRef, useState } from "react";

// Single source of truth for the vote-change grace window across the app.
// Kept private to this hook so callers cannot override it accidentally.
const VOTE_CHANGE_GRACE_PERIOD_MS = 5000;

type GraceElapsedCallback = () => void;

const useVoteGraceTimer = () => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [countdownEndsAt, setCountdownEndsAt] = useState<number | null>(null);

    // Cancels any running timer and resets countdown state.
    const cancel = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setCountdownEndsAt(null);
    }, []);

    // Starts (or restarts) the grace timer.
    // Optional callback runs when timer elapses.
    const start = useCallback((onElapsed?: GraceElapsedCallback) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        const endsAt = Date.now() + VOTE_CHANGE_GRACE_PERIOD_MS;
        setCountdownEndsAt(endsAt);

        timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null;
            setCountdownEndsAt(null);
            if (onElapsed) {
                onElapsed();
            }
        }, VOTE_CHANGE_GRACE_PERIOD_MS);
    }, []);

    // Safety cleanup if component unmounts with an active timer.
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return {
        // Absolute timestamp used by UI countdown renderers.
        countdownEndsAt,
        isActive: Boolean(countdownEndsAt),
        start,
        cancel,
    };
};

export default useVoteGraceTimer;

// Pure factory: returns a timer instance without using React hooks.
// Safe to create lazily anywhere (event handlers, refs) without violating Rules of Hooks.
export const createVoteGraceTimer = () => {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    let countdownEndsAt: number | null = null;

    const cancel = () => {
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
        countdownEndsAt = null;
    };

    const start = (onElapsed?: GraceElapsedCallback) => {
        if (timeout) {
            clearTimeout(timeout);
        }

        const endsAt = Date.now() + VOTE_CHANGE_GRACE_PERIOD_MS;
        countdownEndsAt = endsAt;

        timeout = setTimeout(() => {
            timeout = null;
            countdownEndsAt = null;
            if (onElapsed) onElapsed();
        }, VOTE_CHANGE_GRACE_PERIOD_MS);
    };

    return {
        start,
        cancel,
        get countdownEndsAt() {
            return countdownEndsAt;
        },
        get isActive() {
            return Boolean(countdownEndsAt);
        },
    };
};
